import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { X, Printer, Mail } from "lucide-react";
import { formatMoney } from "@finora/config";
import { Button } from "@finora/ui";
import type { FiscalDocumentDto } from "@finora/types";
import { api } from "../../lib/api";
import { randomUUID } from "../../lib/uuid";
import { useAuthStore } from "../../store/auth-store";

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  CASH: "Готовина",
  CARD: "Картица",
  BANK_TRANSFER: "Пренос на рачун",
  VOUCHER: "Ваучер",
  INSTANT_PAYMENT: "Инстант плаћање",
  CHECK: "Чек",
  OTHER: "Друго безготовинско",
};

// Naslov sekcije na računu MORA da prati stvarnu vrstu PFR dokumenta
// (doc.pfrDocumentType), ne samo da li je refundacija - ranije je bilo
// hardkodovano "ПРОМЕТ", pa je i AVANS račun na štampi pisao "ПРОМЕТ -
// ПРОДАЈА" (razgovor 23.09.2026, "kada se uradi avansni racun treba da
// pise da je avansni na fiskalnom a pise da je promet prodaja").
const PFR_TYPE_LABEL: Record<string, string> = {
  PROMET: "ПРОМЕТ",
  AVANS: "АВАНС",
  OBUKA: "ОБУКА",
  KOPIJA: "КОПИЈА",
  PREDRACUN: "ПРЕДРАЧУН",
};

/**
 * Pravi monospace 40-karakterni red racuna, IDENTICAN formatu koji vraca
 * SUF "Provera racuna" stranica (potvrdjeno 25.09.2026 uporedjivanjem
 * karakter-po-karakter). CSS flex/justify-between (ranije koriscen pristup)
 * NE garantuje isto poravnanje kao pravi monospace grid - zato se ceo
 * tekstualni deo racuna sada generise kao niz gotovih 40-karakternih
 * stringova i renderuje unutar <pre>, ne kao ugnjezdeni <div>/<span>.
 */
const RECEIPT_WIDTH = 40;

function overlayLine(
  parts: Array<{ text: string; start?: number; end?: number }>,
  width = RECEIPT_WIDTH,
): string {
  const buf: string[] = new Array(width).fill(" ");
  for (const p of parts) {
    const start = p.start !== undefined ? p.start : (p.end as number) - p.text.length;
    for (let i = 0; i < p.text.length; i++) {
      const idx = start + i;
      if (idx >= 0 && idx < width) buf[idx] = p.text.charAt(i);
    }
  }
  return buf.join("");
}

/** "Naziv:" ... "vrednost" - naziv levo, vrednost poravnata na kraj reda. */
function kvLine(label: string, value: string, width = RECEIPT_WIDTH): string {
  return overlayLine([{ text: label, start: 0 }, { text: value, end: width }], width);
}

/** Levo poravnat tekst, dopunjen razmacima do fiksne sirine (bez sece). */
function padLine(text: string, width = RECEIPT_WIDTH): string {
  return text.length >= width ? text.slice(0, width) : text + " ".repeat(width - text.length);
}

/** Centriran tekst sa ispunom (npr. "===== NASLOV =====" ili "---TIP---"). */
function centeredLine(text: string, fillChar: string, width = RECEIPT_WIDTH, withSpaces = true): string {
  const inner = withSpaces ? ` ${text} ` : text;
  const total = Math.max(0, width - inner.length);
  const left = Math.ceil(total / 2);
  const right = total - left;
  return fillChar.repeat(left) + inner + fillChar.repeat(right);
}

function fmtDateTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString("sr-RS") : "—";
}

// Zaglavlja tabela su HARDKODOVANA doslovno (ne racunata preko istih
// end-pozicija kao redovi sa podacima) - u pravom SUF ispisu zaglavlje ima
// SVOJE, blago drugacije kolone od podataka ispod njega (potvrdjeno
// karakter-po-karakter poredjenjem, 25.09.2026) - najsigurnije je preneti
// ih doslovno, bez rizika od pogresne pretpostavke o "istoj" koloni.
const ITEM_TABLE_HEADER = "Назив   Цена         Кол.         Укупно";
const TAX_TABLE_HEADER = "Ознака       Име      Стопа        Порез";

type JournalTaxRow = {
  label: string;
  name: string;
  rateNumber: number;
  ratePercent: string;
  taxAmount: string;
};

/**
 * 30.09.2026 (korisnik: "u fiskalnom delu sme samo da se pokaze SAMO ono
 * sta poreska vrati nista vise nista manje i ISKLJUCIVO tako", demonstrirano
 * konkretnim primerom žurnala računa 44/75ПП) - čita poresku tabelu
 * (Ознака/Име/Стопа/Порез) i ukupan iznos poreza DIREKTNO iz sirovog PFR
 * žurnala (journal/refundJournal/copyJournal), ne iz baze/lokalnog
 * izračuna. Žurnal je tekst koji je PFR SAM vratio i koji se već ispisuje
 * bukvalno na PDF-u/pravom papirnom računu - jedini izvor koji je UVEK
 * dostupan kad je dokument fiskalizovan, za razliku od item.taxLabel u
 * bazi (posebna, best-effort kolona upisana POSLE fiskalizacije, koja je
 * za starije račune često ostala prazna - vidi "taxLabel bag" u
 * claude/esir-popravke-status.md, 29.09.2026). Čitanjem direktno iz
 * žurnala se prikazuje TAČNO ono što je poreska vratila, bez obzira da li
 * je taj drugi upis uspeo, i bez ikakvog izmišljenog/dopunjenog podatka.
 * Ako se tabela (ili ukupan iznos poreza) ne može pronaći u žurnalu (npr.
 * žurnal uopšte nije sačuvan za taj dokument), NIŠTA se ne prikazuje
 * umesto pretpostavljenog reda.
 *
 * 30.09.2026, ispravka: projekat koristi tsc --noUncheckedIndexedAccess
 * (vidi claude/esir-popravke-status.md, Deploy napomena o pfr-racun-
 * poravnanje-hotfix.zip) - svi indeksirani pristupi nizu (rawLines[i],
 * match[1], cells[...]) ovde su EKSPLICITNO cuvani protiv 'undefined'
 * pre upotrebe, lokalno provereno sa `tsc --strict
 * --noUncheckedIndexedAccess --noEmit` pre push-a.
 */
function parseTaxSectionFromJournal(journalText: string | null | undefined): {
  rows: JournalTaxRow[];
  totalTaxAmount: string | null;
} {
  if (!journalText) return { rows: [], totalTaxAmount: null };
  const rawLines = journalText.replace(/\r\n/g, "\n").split("\n");
  const headerIdx = rawLines.findIndex((l) => l.includes("Ознака") && l.includes("Порез"));
  if (headerIdx === -1) return { rows: [], totalTaxAmount: null };

  const rows: JournalTaxRow[] = [];
  let totalTaxAmount: string | null = null;

  for (let i = headerIdx + 1; i < rawLines.length; i++) {
    const rawLine = rawLines[i] ?? "";
    const trimmed = rawLine.trim();
    if (trimmed.startsWith("Укупан износ пореза")) {
      const match = trimmed.match(/Укупан износ пореза:\s*(.+)$/);
      totalTaxAmount = match?.[1]?.trim() ?? null;
      break;
    }
    if (trimmed === "" || /^[-=]{5,}/.test(trimmed)) continue;

    const cells = trimmed.split(/\s+/).filter(Boolean);
    if (cells.length < 3) continue;
    const ratePercentRaw = cells[cells.length - 2];
    const taxAmountRaw = cells[cells.length - 1];
    const labelRaw = cells[0];
    if (!ratePercentRaw || !taxAmountRaw || !labelRaw) continue;
    if (!/%$/.test(ratePercentRaw)) continue;
    const rateNumber = parseFloat(ratePercentRaw.replace("%", "").replace(",", "."));
    if (Number.isNaN(rateNumber)) continue;

    rows.push({
      label: labelRaw,
      name: cells.slice(1, cells.length - 2).join(" "),
      rateNumber,
      ratePercent: ratePercentRaw.replace("%", ""),
      taxAmount: taxAmountRaw,
    });
  }

  return { rows, totalTaxAmount };
}

/** Nalazi PFR oznaku (slovo) za datu stopu iz VEĆ PARSIRANE poreske tabele
 * žurnala - koristi se za "(A)" pored naziva artikla, isto pravilo kao
 * gore: prikazuje se samo ako je stvarno potvrđeno iz žurnala. */
function findJournalLabelForRate(rows: JournalTaxRow[], ratePercent: number): string | undefined {
  return rows.find((r) => Math.abs(r.rateNumber - ratePercent) < 0.01)?.label;
}

/**
 * Kompletan prikaz fiskalnog računa (Advanced ESIR + V-PFR) - vidi
 * Tehničko uputstvo ESIR/L-PFR i "Račun sa identifikacijom kupca"/"Fiskalni
 * račun" dokumentaciju Poreske uprave. NAMERNO print-friendly HTML/React
 * prikaz (window.print()), NE PDF - PDF servis (pdf.service.ts) ostaje za
 * dokumente gde je PDF stvarno potreban (fakture i sl.), ovaj prikaz je
 * isključivo za POS fiskalni račun.
 *
 * Podaci se čitaju IZ NORMALIZOVANOG FiscalDocumentDto (backend ih je već
 * izvukao iz sirovog PFR odgovora u FiscalizationAttempt.technicalDetail -
 * vidi PfrFiscalizationProvider/fiscal-documents.service.ts) - ovaj fajl
 * namerno NE parsira nikakav sirov JSON, OSIM poreske tabele/ukupnog
 * poreza koji se (od 30.09.2026) čita direktno iz journal/refundJournal/
 * copyJournal teksta - vidi parseTaxSectionFromJournal() iznad.
 */
export function ReceiptView({
  document: initialDoc,
  onClose,
  allowCopyPrint = false,
}: {
  document: FiscalDocumentDto;
  onClose: () => void;
  /** Kad se racun otvara IZ LISTE (vec fiskalizovan, ne odmah posle
   * naplate na kasi) - "Odstampaj" postaje "Odstampaj kopiju" i PRE
   * stampe se izvrsi PRAVI PFR KOPIJA poziv, umesto puke ponovne stampe
   * stare (kesirane) sadrzine - vidi razgovor 25.09.2026
   * "nemas dugme za kopiju racuna". */
  allowCopyPrint?: boolean;
}) {
  const { data: company } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const currentUser = useAuthStore((s) => s.user);

  // Nakon uspesnog copy() poziva prikaz prelazi na kopiju (copy* kolone -
  // sopstveni PFR broj/QR/vreme); stampa se pokrece TEK kad se doc azurira
  // (useEffect ispod), ne odmah u onSuccess (React state je asinhron).
  const [displayDoc, setDisplayDoc] = useState(initialDoc);
  const [printMode, setPrintMode] = useState<"normal" | "copy">("normal");
  const [copyError, setCopyError] = useState<string | null>(null);

  const copyMutation = useMutation({
    mutationFn: () => api.copyFiscalDocument(initialDoc.id, randomUUID()),
    onSuccess: (updated) => {
      setCopyError(null);
      setDisplayDoc(updated);
      setPrintMode("copy");
    },
    onError: (err) => {
      setCopyError(err instanceof Error ? err.message : "Štampanje kopije nije uspelo.");
    },
  });

  // "Prikaži kopiju" samo PRIKAZUJE već izdatu kopiju (bez novog PFR poziva i bez štampe).
  const skipAutoPrint = useRef(false);
  useEffect(() => {
    if (printMode === "copy" && displayDoc.copyFiscalNumber) {
      if (skipAutoPrint.current) {
        skipAutoPrint.current = false;
        return;
      }
      window.print();
    }
  }, [printMode, displayDoc.copyFiscalNumber]);

  // ESC zatvara "Pregled racuna" modal (isto kao klik na X) - razgovor
  // 25.09.2026.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const doc = displayDoc;
  const isCopy = printMode === "copy";
  // 30.09.2026 (korisnički zahtev - "kada stampam ponovo refundaciju treba
  // da pise kopija refundacija a pise kopija prodaja"): `isRefundedDoc` je
  // NEZAVISNO od toga da li se trenutno prikazuje kopija - ranije je
  // `isRefund` (koje JESTE zavisilo od `!isCopy`) bilo jedini signal, pa je
  // natpis za kopiju VEĆ REFUNDIRANOG dokumenta ispao samo "КОПИЈА" (bez
  // reči koja kaže ŠTA se kopira) umesto "КОПИЈА РЕФУНДАЦИЈА". Sad se ova
  // činjenica čita direktno iz `doc.status`, nezavisno od printMode, i
  // koristi za `actionLabel` ispod - u skladu sa backend ispravkom
  // (FiscalDocumentsService.copy()) koja sada STVARNO šalje PFR-u kopiju
  // refundacije (ne prodaje) u ovom slučaju, pa se natpis ovde slaže sa
  // onim što je PFR stvarno primio/vratio.
  const isRefundedDoc = doc.status === "REFUNDED";
  const isRefund = !isCopy && isRefundedDoc;
  // Storno je poseban fiskalni dokument prema PFR-u (svoj fiscalNumber/QR) -
  // vidi napomenu u schema.prisma. Za REFUNDED dokument prikazujemo PODATKE
  // SAME REFUNDACIJE (ne originalne prodaje), sa referencom na original.
  const displayFiscalNumber = isCopy
    ? doc.copyFiscalNumber
    : isRefund
      ? doc.refundFiscalNumber
      : doc.fiscalNumber;
  const displayInvoiceCounter = isCopy
    ? doc.copyInvoiceCounter
    : isRefund
      ? doc.refundInvoiceCounter
      : doc.invoiceCounter;
  const displayQrCode = isCopy ? doc.copyQrCodeBase64 : isRefund ? doc.refundQrCodeBase64 : doc.qrCodeBase64;
  const displayVerificationUrl = isCopy
    ? doc.copyVerificationUrl
    : isRefund
      ? doc.refundVerificationUrl
      : doc.verificationUrl;
  const displayTime = isCopy ? doc.copiedAt : isRefund ? doc.refundedAt : doc.fiscalizedAt;
  const pfrTypeLabel = PFR_TYPE_LABEL[doc.pfrDocumentType ?? "PROMET"] ?? "ПРОМЕТ";

  // Sirov PFR žurnal KOJI ODGOVARA prikazanoj varijanti (original/storno/
  // kopija) - vidi parseTaxSectionFromJournal() iznad. Ovo je izvor za
  // celu poresku tabelu/ukupan porez, i za "(oznaka)" pored artikla ispod.
  const journalForVariant = isCopy ? doc.copyJournal : isRefund ? doc.refundJournal : doc.journal;
  const { rows: journalTaxRows, totalTaxAmount: journalTotalTaxAmount } =
    parseTaxSectionFromJournal(journalForVariant);

  // "Референтни број"/"Референтно време" - broj i vreme PRETHODNOG računa u
  // lancu na koji se OVAJ (upravo odštampani) račun nadovezuje. Dva
  // različita slučaja dele isti prikaz ispod:
  //  - Аванс-Рефундација (isRefund, ovaj doc JE avans sada storniran) -
  //    referencira SVOJ originalni Аванс-Продаја (doc.fiscalNumber/
  //    doc.fiscalizedAt - to su podaci originalne prodaje, netaknuti kad
  //    je dokument stornuran, vidi refund() u fiscal-documents.service.ts).
  //  - Konačni Промет-Продаја koji zatvara avans (closeAdvanceWithCashSale) -
  //    referencira Аванс-Рефундацију koja ga je zatvorila
  //    (doc.referenceFiscalNumber/doc.referenceFiscalizedAt - ranije se OVO
  //    uopšte nije prikazivalo na štampi, razgovor 23.09.2026 "kada se
  //    uradi iz avansa u konacnio treba da pisu reference ne pisu").
  //  - Kopija REFUNDIRANOG dokumenta (isCopy && isRefundedDoc, od
  //    30.09.2026) - referencira SAMU REFUNDACIJU (doc.refundFiscalNumber/
  //    doc.refundedAt), jer je TO ono što se sada stvarno kopira kod
  //    PFR-a (vidi backend ispravku FiscalDocumentsService.copy()), ne
  //    originalnu prodaju kao ranije.
  //  - Kopija NErefundirane prodaje i dalje referencira originalnu prodaju
  //    (doc.fiscalNumber/fiscalizedAt), nepromenjeno.
  const referenceNumber =
    isCopy && isRefundedDoc
      ? doc.refundFiscalNumber
      : isCopy || isRefund
        ? doc.fiscalNumber
        : doc.referenceFiscalNumber;
  const referenceTime =
    isCopy && isRefundedDoc
      ? doc.refundedAt
      : isCopy || isRefund
        ? doc.fiscalizedAt
        : doc.referenceFiscalizedAt;

  // "ЕСИР време" - kad je KASA pripremila/poslala zahtev (razlicito od
  // "ПФР време" koje server vraca) - nema posebno backend polje, koristi se
  // createdAt ODGOVARAJUCEG FiscalizationAttempt reda (onog ciji je
  // fiscalNumber isti kao ono sto se trenutno prikazuje - original/storno/
  // kopija - vec je sve u fiscalizationAttempts nizu koji dokument nosi).

  // ЕСИР број / ЕСИР време iz sirovog PFR žurnala (ono što je stvarno poslato
  // poreskoj; za avans je ЕСИР време 25h ranije od ПФР времena).
  const journalKv = (label: string): string | null => {
    const line = (journalForVariant ?? "")
      .replace(/\r\n/g, "\n")
      .split("\n")
      .find((l) => l.trim().startsWith(label));
    const v = line ? line.trim().slice(label.length).trim() : "";
    return v || null;
  };
  const journalEsirNumber = journalKv("ЕСИР број:");
  const journalEsirTime = journalKv("ЕСИР време:");

  // 30.09.2026 (korisnički zahtev - "kada stampam ponovo refundaciju treba
  // da pise kopija refundacija a pise kopija prodaja"): `actionLabel` za
  // kopiju sada zavisi i od `isRefundedDoc`, ne samo od `isCopy` - ranije
  // je SVAKA kopija (i prodaje i refundacije) pisala samo "КОПИЈА", pa je
  // ceo naslov ispao "ПРОМЕТ КОПИЈА" bez reči koja kaže ŠTA se kopira.
  const actionLabel = isCopy
    ? isRefundedDoc
      ? "КОПИЈА РЕФУНДАЦИЈА"
      : "КОПИЈА ПРОДАЈА"
    : isRefund
      ? "РЕФУНДАЦИЈА"
      : "ПРОДАЈА";
  // Na pravom SUF ispisu UKUPAN iznos/placanja/poreska tabela za STORNO su
  // POZITIVNI (samo pojedinacne stavke su negativne) - vidi razgovor
  // 25.09.2026, uporedjeno karakter-po-karakter sa SUF "Provera racuna".
  // Kopija REFUNDIRANOG dokumenta prikazuje iznose isto kao i sama
  // refundacija (pozitivne, sem pojedinačnih stavki) - `grossLabel`/
  // `itemTotal` ispod već koriste `isRefund`, koji je i dalje `false` za
  // kopiju (namerno - `isCopy` grana bira copy* polja nezavisno), pa se
  // ovo ponašanje NIJE menjalo ovom ispravkom.
  const grossLabel = isRefund ? "Укупна рефундација:" : "Укупан износ:";

  // "Kupac: {ime} Pib: {pib}" u reklamnom prostoru - registrovan Customer
  // ima prednost, inace rucno uneti buyerName/buyerPib (POS bez punog
  // Customer zapisa) - razgovor 25.09.2026.
  const buyerDisplayName = doc.customer?.name ?? doc.buyerName ?? null;
  const buyerDisplayAddress = doc.customer?.address ?? doc.buyerAddress ?? null;
  const buyerDisplayPib = doc.customer?.taxId ?? doc.buyerPib ?? null;

  // "Odobren popust: X% (-iznos RSD)" u reklamnom prostoru - PFR NIKAD ne
  // dobija posebno "discount" polje (uvek dobija vec umanjenu, konacnu
  // cenu - fiskalni payload se OVIM NE MENJA), popust se ovde SAMO
  // objasnjava kupcu jer je "polje za reklamu" van fiskalnih podataka koje
  // PFR/SUF prima (instrukcija konsultanta/Poreske pripreme, 25.09.2026).
  // Procenat je izveden iz vec izracunatih iznosa (cena PRE popusta =
  // doc.grossAmount + doc.discountAmount), ne cuva se posebno.
  const discountAmountNum = Number(doc.discountAmount);
  const pretotalAmountNum = Number(doc.grossAmount) + discountAmountNum;
  const discountPercent = pretotalAmountNum > 0 ? (discountAmountNum / pretotalAmountNum) * 100 : 0;
  const discountPercentLabel =
    Math.abs(discountPercent - Math.round(discountPercent)) < 0.05
      ? String(Math.round(discountPercent))
      : discountPercent.toFixed(1);

  // "Posalji racun na email" - razgovor 26.09.2026 ("da moze za mail na
  // koraku naplati"). Kasir bira A4 ili "realan izgled" (papirna 80mm
  // replika, isti PDF koji koristi Print Agent) - backend salje preko
  // korisnickog SMTP-a ako je podesen u Podesavanjima, inace sa naseg
  // domena (EmailService fallback na .env).
  //
  // 29.09.2026 (FINORA-IMP-018, nastavak - "mail MORA da bude identican
  // kasi"): podrazumevana vrednost je bila "a4" - siroka, drugacije
  // proporcionisana strana od stvarnog 80mm papirnog racuna, pa je mail
  // delovao vizuelno "drugaciji" od kase i posle drugih ispravki (QR
  // pozicija, logo) u pdf.service.ts, jer je cela strana drugog oblika.
  // Podrazumevano sada "thermal80" (ista PDF logika kao "Stampaj kopiju"/
  // Print Agent) - kasir i dalje moze rucno prebaciti na A4 ako zeli.
  const [emailAddress, setEmailAddress] = useState(doc.customer?.email ?? "");
  const [emailFormat, setEmailFormat] = useState<"a4" | "thermal80">("thermal80");
  const [emailStatus, setEmailStatus] = useState<{ type: "success" | "error"; message: string } | null>(
    null,
  );
  const sendEmailMutation = useMutation({
    mutationFn: () =>
      api.sendFiscalDocumentEmail(doc.id, {
        email: emailAddress.trim() || undefined,
        format: emailFormat,
      }),
    onSuccess: (result) => {
      setEmailStatus({ type: "success", message: `Račun poslat na ${result.sentTo}.` });
    },
    onError: (err) => {
      setEmailStatus({
        type: "error",
        message: err instanceof Error ? err.message : "Slanje mejla nije uspelo.",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 print:static print:bg-white print:p-0">
      <div className="flex max-h-[90vh] w-full max-w-sm flex-col overflow-auto rounded-xl bg-surface shadow-xl print:max-h-none print:w-full print:max-w-none print:overflow-visible print:rounded-none print:shadow-none">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 print:hidden">
          <h2 className="text-sm font-semibold text-slate-900">
            {isCopy ? "Fiskalni račun (kopija)" : isRefund ? "Fiskalni račun (storno)" : "Fiskalni račun"}
          </h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="flex flex-col items-center px-5 py-5 font-mono text-xs text-slate-800 print-area">
          {company?.showLogoOnReceipt && company.logoUrl && (
            <div className="mb-1 flex justify-center">
              <img
                src={company.logoUrl}
                alt="Logo"
                className="h-12 max-w-[160px] object-contain print:h-14"
              />
            </div>
          )}
          {(() => {
            // Ceo gornji deo racuna (do PFR vremena/broja/brojaca, PRE QR koda)
            // - generisan kao niz GOTOVIH 40-karakternih redova, IDENTICAN
            // formatu SUF "Provera racuna" stranice (vidi HELPERS iznad).
            const lines: string[] = [];
            lines.push(centeredLine("ФИСКАЛНИ РАЧУН", "="));
            if (company) {
              lines.push(centeredLine(company.taxId ?? "", " ", RECEIPT_WIDTH, false));
              lines.push(centeredLine(company.name ?? "", " ", RECEIPT_WIDTH, false));
              lines.push(centeredLine(company.businessLocationName ?? "", " ", RECEIPT_WIDTH, false));
              lines.push(centeredLine(company.businessLocationAddress ?? "", " ", RECEIPT_WIDTH, false));
            }
            lines.push(kvLine("Касир:", journalKv("Касир:") ?? doc.cashierName ?? currentUser?.fullName ?? ""));
            if (doc.buyerId) lines.push(kvLine("ИД купца:", doc.buyerId));
            if (journalEsirNumber) lines.push(kvLine("ЕСИР број:", journalEsirNumber));
            // ЕСИР време sme samo na Avans Prodaja (Poreska, 02.11.2026) - samo iz žurnala.
            if (journalEsirTime) lines.push(kvLine("ЕСИР време:", journalEsirTime));
            if (referenceNumber) {
              lines.push(kvLine("Реф. број:", referenceNumber));
              lines.push(kvLine("Реф. време:", fmtDateTime(referenceTime)));
            }
            lines.push(centeredLine(`${pfrTypeLabel} ${actionLabel}`, "-", RECEIPT_WIDTH, false));
            lines.push("Артикли");
            lines.push("=".repeat(RECEIPT_WIDTH));
            lines.push(ITEM_TABLE_HEADER);
            lines.push("=".repeat(RECEIPT_WIDTH));
            doc.items.forEach((item) => {
              // Samo POJEDINACNE stavke su negativne na stornu (potvrdjeno
              // zvanicnom dokumentacijom i SUF ispisom) - ukupni iznosi ispod
              // ostaju pozitivni (vidi grossLabel/isRefund napomenu iznad).
              const itemTotal = isRefund
                ? `-${formatMoney(item.grossAmount)}`
                : formatMoney(item.grossAmount);
              // 30.09.2026 - "(oznaka)" pored artikla se sada uzima iz VEĆ
              // PARSIRANE poreske tabele žurnala (ista logika/izvor kao
              // glavna poreska tabela ispod), ne iz item.taxLabel u bazi -
              // vidi napomenu uz parseTaxSectionFromJournal() na vrhu fajla.
              // item.taxRatePercent dolazi kao string iz DTO-a, konvertuje
              // se u broj pre poredjenja sa parsiranim brojevima iz zurnala.
              const itemJournalLabel = findJournalLabelForRate(
                journalTaxRows,
                Number(item.taxRatePercent),
              );
              lines.push(
                padLine(`${item.name}${itemJournalLabel ? ` (${itemJournalLabel})` : ""}`),
              );
              lines.push(
                overlayLine([
                  { text: formatMoney(item.unitPriceGross), end: 13 },
                  { text: `${item.quantity} ${item.unit}`.trim(), end: 24 },
                  { text: itemTotal, end: RECEIPT_WIDTH },
                ]),
              );
            });
            lines.push("-".repeat(RECEIPT_WIDTH));
            lines.push(kvLine(grossLabel, formatMoney(doc.grossAmount)));
            doc.payments.forEach((p) => {
              lines.push(kvLine(`${PAYMENT_METHOD_LABEL[p.method] ?? p.method}:`, formatMoney(p.amount)));
              // Primljeni iznos/kusur - standardni deo fiskalnog racuna za
              // gotovinsko placanje, prikazuje se kad je kasir uneo iznos
              // primljene gotovine (receivedAmount) - vec izracunato i
              // sacuvano u registerPayment()/calculateChange(), samo se do
              // sada nije prikazivalo na stampi (razgovor 26.09.2026).
              if (p.method === "CASH" && p.receivedAmount) {
                lines.push(kvLine("Примљени износ:", formatMoney(p.receivedAmount)));
                lines.push(kvLine("Повраћај:", formatMoney(p.changeAmount ?? "0")));
              }
            });
            lines.push("=".repeat(RECEIPT_WIDTH));
            // 30.09.2026 (korisnik: "u fiskalnom delu sme samo da se pokaze
            // SAMO ono sta poreska vrati nista vise nista manje i
            // ISKLJUCIVO tako") - CELA poreska tabela I ukupan iznos poreza
            // se sada citaju DIREKTNO iz sirovog PFR zurnala
            // (journalTaxRows/journalTotalTaxAmount, parsirano na vrhu
            // fajla iz journalForVariant) - ne iz baze (item.taxLabel) niti
            // iz lokalno izracunatog doc.taxAmount. Ako zurnal uopste nije
            // sacuvan za ovaj dokument (retko, samo za vrlo stare/nepotpune
            // zapise), ne prikazuje se NI TABELA NI ukupan iznos poreza -
            // umesto izmisljenog/pretpostavljenog podatka.
            if (journalTaxRows.length > 0) {
              lines.push(TAX_TABLE_HEADER);
              journalTaxRows.forEach((row) => {
                lines.push(
                  overlayLine([
                    { text: row.label, start: 0 },
                    { text: row.name, end: 17 },
                    { text: `${row.ratePercent}%`, end: 26 },
                    { text: row.taxAmount, end: RECEIPT_WIDTH },
                  ]),
                );
              });
              lines.push("-".repeat(RECEIPT_WIDTH));
            }
            if (journalTotalTaxAmount) {
              lines.push(kvLine("Укупан износ пореза:", journalTotalTaxAmount));
            }
            lines.push("=".repeat(RECEIPT_WIDTH));
            lines.push(kvLine("ПФР време:", fmtDateTime(displayTime)));
            lines.push(kvLine("ПФР број рачуна:", displayFiscalNumber ?? "—"));
            lines.push(kvLine("Бројач рачуна:", displayInvoiceCounter ?? "—"));
            lines.push("=".repeat(RECEIPT_WIDTH));
            return <pre className="whitespace-pre leading-relaxed">{lines.join("\n")}</pre>;
          })()}

          {displayQrCode && (
            <div className="flex flex-col items-center gap-1 pt-2">
              <img
                src={`data:image/gif;base64,${displayQrCode}`}
                alt="QR kod za proveru računa"
                className="aspect-square [image-rendering:pixelated]"
                style={{ width: `${RECEIPT_WIDTH}ch` }}
              />
              {displayVerificationUrl && (
                <a
                  href={displayVerificationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-center text-[10px] text-brand underline"
                >
                  Provera računa
                </a>
              )}
            </div>
          )}

          <pre className="whitespace-pre pt-2 text-center">{centeredLine("КРАЈ ФИСКАЛНОГ РАЧУНА", "=")}</pre>

          {/* "Polje za reklamu" - ispod zvanicnog zavrsetka racuna. Kupac (ako
              postoji registrovan Customer ili rucno unet buyerName/buyerPib -
              razgovor 25.09.2026 "Kupac: ... Pib: ...") + napomena za
              avansne racune (nepromenjeno u odnosu na ranije). Adresa je
              poseban red (razgovor 26.09.2026) - prikazuje se SAMO ako
              postoji (Customer.address ili rucno uneto buyerAddress), da se
              ne ostavi prazan "Adresa: —" red kad nije poznata. */}
          {(buyerDisplayName || buyerDisplayPib) && (
            <>
              <p>
                Kupac: {buyerDisplayName ?? "—"}
                {buyerDisplayPib ? ` Pib: ${buyerDisplayPib}` : ""}
              </p>
              {buyerDisplayAddress && <p>Adresa: {buyerDisplayAddress}</p>}
            </>
          )}
          {/* Serijski broj opreme (03.10.2026, MerchantPro s/n:... napomena) -
              ISKLJUCIVO ovde, u reklamnom prostoru ISPOD zvanicnog zavrsetka
              racuna - NIKAD u "ID kupca" (PFR to odbija, vidi
              resolveBuyerId() u fiscal-document.mapper.ts). */}
          {doc.buyerIdExtra && <p>Serijski broj: {doc.buyerIdExtra}</p>}
          {discountAmountNum > 0 && (
            <div className="text-center">
              <p className="font-semibold">INFO:</p>
              <p>Odobren popust:</p>
              <p>
                {discountPercentLabel}% (-{formatMoney(doc.discountAmount)})
              </p>
            </div>
          )}
          {(doc.isAdvance || doc.linkedAdvanceDocumentId) && doc.note && (
            <p className="text-red-600">* {doc.note}</p>
          )}
        </div>

        <div className="space-y-3 border-t border-border p-4 print:hidden">
          {copyError && <p className="text-xs text-red-600">{copyError}</p>}
          <Button
            className="w-full"
            disabled={copyMutation.isPending}
            onClick={() => {
              if (allowCopyPrint) {
                copyMutation.mutate();
                return;
              }
              // Štampa ORIGINALA = original je izdat kupcu (mejl posle toga nosi kopiju).
              api.markFiscalDocumentIssued(doc.id, isRefundedDoc ? "refund" : "sale").catch(() => undefined);
              window.print();
            }}
          >
            <Printer className="h-4 w-4" aria-hidden />{" "}
            {allowCopyPrint
              ? copyMutation.isPending
                ? "Štampam kopiju..."
                : "Odštampaj kopiju"
              : "Odštampaj"}
          </Button>

          {allowCopyPrint && displayDoc.copyFiscalNumber && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => {
                skipAutoPrint.current = true;
                setPrintMode(printMode === "copy" ? "normal" : "copy");
              }}
            >
              {printMode === "copy" ? "Prikaži original" : "Prikaži izdatu kopiju (bez nove)"}
            </Button>
          )}

          {displayFiscalNumber && (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <p className="text-xs font-medium text-slate-700">Pošalji račun na email</p>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="kupac@primer.com"
                className="w-full rounded-md border border-border px-2 py-1.5 text-xs"
              />
              <div className="flex gap-3 text-xs text-slate-600">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="email-format"
                    checked={emailFormat === "a4"}
                    onChange={() => setEmailFormat("a4")}
                  />
                  A4 format
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="email-format"
                    checked={emailFormat === "thermal80"}
                    onChange={() => setEmailFormat("thermal80")}
                  />
                  Realan izgled (papirni račun)
                </label>
              </div>
              {emailStatus && (
                <p className={emailStatus.type === "success" ? "text-xs text-green-600" : "text-xs text-red-600"}>
                  {emailStatus.message}
                </p>
              )}
              <Button
                className="w-full"
                disabled={sendEmailMutation.isPending || !emailAddress.trim()}
                onClick={() => {
                  setEmailStatus(null);
                  sendEmailMutation.mutate();
                }}
              >
                <Mail className="h-4 w-4" aria-hidden />{" "}
                {sendEmailMutation.isPending ? "Šaljem..." : "Pošalji mejlom"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
